import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/custom/Navbar";
import Footer from "@/components/custom/Footer";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import StudentDashboard from "@/pages/StudentDashboard";
import MentorDashboard from "@/pages/MentorDashboard";
import AdminPanel from "@/pages/AdminPanel";
import UniversityDashboard from "@/pages/UniversityDashboard";
import PrashaPanel from "@/pages/PrashaPanel";
import ResearchPage from "@/pages/ResearchPage";
import CoursesPage from "@/pages/CoursesPage";
import CourseDetailPage from "@/pages/CourseDetailPage";
import CourseBuilder from "@/pages/CourseBuilder";
import LessonViewer from "@/pages/LessonViewer";
import PricingPage from "@/pages/PricingPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import MyCertificates from "@/pages/MyCertificates";
import ResearchProjectRoom from "@/pages/ResearchProjectRoom";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
      <div className="animate-spin h-8 w-8 border-4 border-[#1a5c5c] border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

// Admin-only route for Prasha verification panel
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900">
      <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full" />
    </div>
  );
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
};

const DashboardRouter = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/auth" replace />;
  // Admin goes to legacy panel from dashboard, but Prasha panel is at /internal/prasha
  if (user.role === "admin") return <AdminPanel />;
  if (user.role === "mentor") return <MentorDashboard />;
  if (user.role === "university") return <UniversityDashboard />;
  return <StudentDashboard />;
};

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<><Navbar /><LandingPage /><Footer /></>} />
      <Route path="/auth" element={<><Navbar /><AuthPage /><Footer /></>} />
      <Route path="/research" element={<><Navbar /><ResearchPage /><Footer /></>} />
      <Route path="/courses" element={<><Navbar /><CoursesPage /><Footer /></>} />
      <Route path="/courses/:courseId" element={<><Navbar /><CourseDetailPage /><Footer /></>} />
      <Route path="/courses/:courseId/lessons/:lessonId" element={<ProtectedRoute><LessonViewer /></ProtectedRoute>} />
      <Route path="/pricing" element={<><Navbar /><PricingPage /><Footer /></>} />
      <Route path="/dashboard" element={<ProtectedRoute><Navbar /><DashboardRouter /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><Navbar /><MyCertificates /><Footer /></ProtectedRoute>} />
      <Route path="/research-room/:roomId" element={<ProtectedRoute><ResearchProjectRoom /></ProtectedRoute>} />
      <Route path="/university/course-builder" element={<ProtectedRoute allowedRoles={['university']}><CourseBuilder /></ProtectedRoute>} />
      <Route path="/university/course-builder/:courseId" element={<ProtectedRoute allowedRoles={['university']}><CourseBuilder /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><Navbar /><PaymentSuccess /><Footer /></ProtectedRoute>} />
      {/* Hidden Admin Prasha Panel - not linked from UI */}
      <Route path="/internal/prasha" element={<AdminRoute><PrashaPanel /></AdminRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
